import { Component, Input, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { backendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';
import { UploadUiComponent } from '../upload-ui/upload-ui.component';
import { BytetounitPipe } from '../bytetounit.pipe';

@Component({
  selector: 'app-patient-documents',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, UploadUiComponent, BytetounitPipe],
  templateUrl: './patient-documents.component.html',
  styleUrls: ['./patient-documents.component.scss']
})
export class PatientDocumentsComponent implements OnInit {
  @Input() patientId!: string;
  @Input() isPractitioner: boolean = true; // Set default true or false based on usage

  documents: any[] = [];
  loading = false;
  showUploadArea = false;

  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private errorService = inject(ErrorService);
  private route = inject(ActivatedRoute);


  ngOnInit() {
    if (!this.patientId) {
      // Try to get patientId from parent or current route params
      this.patientId = this.route.parent?.snapshot.params['id'];
    }

    if (this.patientId) {
      this.loadDocuments();
    } else {
      console.warn('PatientDocumentsComponent: No patientId provided or found in route.');
    }
  }


  loadDocuments() {
    this.loading = true;
    this.http.get<any>(`${this.backendUrl}/DocumentReference?subject=Patient/${this.patientId}`).subscribe({
      next: (bundle) => {
        this.documents = bundle.entry?.map((e: any) => e.resource) || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load documents', err);
        this.errorService.openandCloseError('Failed to load patient documents.');
        this.loading = false;
      }
    });
  }

  onFilesUploaded(files: any[]) {
    const newFiles = files.filter(f => !f.documentReferenceId);
    
    if (newFiles.length === 0) return;

    for (const file of newFiles) {
      const docRef = {
        resourceType: 'DocumentReference',
        status: 'current',
        subject: { reference: `Patient/${this.patientId}` },
        content: [{
          attachment: {
            url: file.fileLink,
            contentType: file.uploadFileType,
            size: file.uploadFileSize,
            title: file.uploadFileName,
            creation: file.uploadDate
          }
        }]
      };

      this.http.post<any>(`${this.backendUrl}/DocumentReference`, docRef).subscribe({
        next: (createdDoc) => {
          file.documentReferenceId = createdDoc.id; // Mark as saved
          this.loadDocuments(); // Refresh the list
        },
        error: (err) => {
          console.error('Error creating DocumentReference', err);
          this.errorService.openandCloseError(`Failed to save document reference for ${file.uploadFileName}`);
        }
      });
    }
    
    // Auto-close after short delay to show success visually in upload UI
    setTimeout(() => {
        this.showUploadArea = false;
    }, 1500);
  }

  downloadDocument(doc: any) {
    const attachment = doc.content?.[0]?.attachment;
    let url = attachment?.url;
    const fileName = attachment?.title || 'document';

    if (url) {
      // Cloudinary security policy for PDFs requires 'fl_attachment' to download them directly from the image endpoint.
      if (url.includes('cloudinary.com') && url.toLowerCase().includes('.pdf')) {
        url = url.replace('/upload/', '/upload/fl_attachment/');
      }

      // Create a hidden link and click it to force download
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      this.errorService.openandCloseError('Document URL not found.');
    }
  }


  deleteDocument(doc: any) {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    this.http.delete(`${this.backendUrl}/DocumentReference/${doc.id}`).subscribe({
      next: () => {
        this.loadDocuments();
      },
      error: (err) => {
        console.error('Failed to delete document', err);
        this.errorService.openandCloseError('Failed to delete document.');
      }
    });
  }
}
